import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Flag, LayoutGrid, List, MapPin, Pause, Play, Timer, Trophy, Zap } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelectedClub } from '@/features/club';
import {
  buildNameMap,
  buildScheduleTimeline,
  collectPlayerIds,
  competitionKeys,
  courtAccent,
  elapsedMatchMinutes,
  formatDurationMinutes,
  matchKeys,
  MatchStatusMark,
  roundAccent,
  roundLabel,
  runMatchAction,
  ScheduleGrid,
  scoreSummary,
  toViewStatus,
  useClubCompetitionsQuery,
  useClubCourtsQuery,
  useClubMatchesQuery,
  useMatchPlannerQuery,
  type ApiMatch,
  type MatchLifecycleAction,
  type ScheduleCourt,
  type ScheduleTimeline,
} from '@/features/competition';
import { getUsersByIds, userKeys } from '@/features/user';
import { ApiError } from '@/shared/api/ApiError';
import { cn } from '@/shared/lib/cn';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  DateInput,
  EmptyState,
  IconButton,
  PageHeader,
  SegmentedControl,
  Select,
  Skeleton,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableHeaderCell,
  TableRow,
  useToast,
} from '@/shared/ui';

/** Akcje dostępne z tej listy — bez startu, bo mecze już trwają. */
type LiveAction = Extract<MatchLifecycleAction, 'suspend' | 'resume' | 'finish'>;

/** Filtr statusu listy. */
type StatusFilter = 'all' | 'live' | 'suspended';

/** Widok zakladki: lista albo os czasu kortow. */
type ViewMode = 'list' | 'grid';

/** Klucz grupy dziennej dla meczow bez znanego startu. */
const UNKNOWN_DAY = 'unknown';

/** Klucz toastu po udanej akcji. */
const ACTION_TOAST = {
  suspend: 'matchResult.toast.suspended',
  resume: 'matchResult.toast.resumed',
  finish: 'matchResult.toast.finished',
} as const satisfies Record<LiveAction, string>;

/** Formatuje date ISO (yyyy-MM-dd) na zapis polski (dd.MM.yyyy). */
function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  if (!year || !month || !day) return iso;
  return `${day}.${month}.${year}`;
}

/** Data lokalna w formacie ISO (yyyy-MM-dd). */
function localIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Zegar odswiezany co pol minuty — czas gry liczymy w przegladarce
 * z `startedAt`, wiec licznik biegnie bez zadnych zapytan do backendu
 * (ten sam wzorzec co licznik na kartach meczow na zywo).
 */
function useNowHalfMinute(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [enabled]);

  return now;
}

/**
 * Rozpoczęte mecze — trwające i przerwane mecze ze wszystkich turniejów klubu,
 * pogrupowane po dniu startu, z kafelkami statystyk i filtrami (turniej, kort,
 * status) jak w zakładce „Zaplanowane". Przełącznik widoku pokazuje oś czasu
 * kortów (tylko do odczytu). Wiersz prowadzi do wpisania wyniku, a akcje
 * pozwalają przerwać, wznowić i zakończyć mecz bez wchodzenia w niego.
 */
export function LiveMatchesPage() {
  const { t } = useTranslation('common');
  const { t: tt } = useTranslation('tournament');
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { clubId, isEmpty: hasNoClubs } = useSelectedClub();

  const nowDate = new Date();
  const todayIso = localIsoDate(nowDate);
  const yesterdayIso = localIsoDate(new Date(nowDate.getTime() - 86_400_000));
  const nowMinutes = nowDate.getHours() * 60 + nowDate.getMinutes();

  const [view, setView] = useState<ViewMode>('list');
  const [tournamentFilter, setTournamentFilter] = useState('');
  const [courtFilter, setCourtFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [plannerDate, setPlannerDate] = useState(todayIso);

  // Backendowy filtr przyjmuje jeden status — trwające i przerwane
  // pobieramy osobno i sklejamy w jedną listę.
  const liveQuery = useClubMatchesQuery(clubId, { status: 'IN_PROGRESS' });
  const suspendedQuery = useClubMatchesQuery(clubId, { status: 'SUSPENDED' });
  const competitionsQuery = useClubCompetitionsQuery(clubId);
  const courtsQuery = useClubCourtsQuery(clubId);
  // Os czasu pobiera planner tylko wtedy, gdy widok jest aktywny.
  const plannerQuery = useMatchPlannerQuery(view === 'grid' ? clubId : undefined, plannerDate);
  const planner = plannerQuery.data;

  const matches = useMemo(
    () =>
      [...(liveQuery.data ?? []), ...(suspendedQuery.data ?? [])].sort(
        (a, b) => Date.parse(a.startedAt ?? '') - Date.parse(b.startedAt ?? ''),
      ),
    [liveQuery.data, suspendedQuery.data],
  );

  // Filtry dzialaja na widoku listy; statystyki licza sie z pelnej listy.
  const filtered = useMemo(
    () =>
      matches.filter((match) => {
        if (tournamentFilter && match.competitionId !== tournamentFilter) return false;
        if (courtFilter && match.courtId !== courtFilter) return false;
        if (statusFilter === 'live' && match.status !== 'IN_PROGRESS') return false;
        if (statusFilter === 'suspended' && match.status !== 'SUSPENDED') return false;
        return true;
      }),
    [matches, tournamentFilter, courtFilter, statusFilter],
  );

  // Sekcje dzienne po dniu startu — przerwany mecz mogl zaczac sie wczoraj.
  const dayGroups = useMemo(() => {
    const map = new Map<string, ApiMatch[]>();
    for (const match of filtered) {
      const key = match.startedAt ? localIsoDate(new Date(match.startedAt)) : UNKNOWN_DAY;
      const group = map.get(key);
      if (group) group.push(match);
      else map.set(key, [match]);
    }
    return [...map.entries()];
  }, [filtered]);

  // Nazwy turniejow do kolumny — mecz niesie tylko competitionId.
  const tournamentNames = useMemo(
    () => new Map((competitionsQuery.data ?? []).map((item) => [item.id, item.name])),
    [competitionsQuery.data],
  );

  // Nazwy kortow — mecz wskazuje kort samym identyfikatorem.
  const courtNames = useMemo(
    () =>
      new Map(
        (courtsQuery.data ?? []).map((court) => [
          court.id,
          court.name || t('scheduledMatchesPage.court', { number: court.number }),
        ]),
      ),
    [courtsQuery.data, t],
  );

  // Jeden batch nazwisk dla tabeli i osi czasu (planner moze niesc innych graczy).
  const plannerMatches = useMemo(
    () => (planner?.scheduledMatches ?? []).map((entry) => entry.match),
    [planner],
  );
  const playerIds = useMemo(
    () => collectPlayerIds([...matches, ...plannerMatches]).sort(),
    [matches, plannerMatches],
  );
  const playersQuery = useQuery({
    queryKey: [...userKeys.all, 'batch', playerIds.join(',')],
    queryFn: () => getUsersByIds(playerIds),
    enabled: playerIds.length > 0,
  });
  const names = useMemo(() => buildNameMap(playersQuery.data ?? []), [playersQuery.data]);

  const now = useNowHalfMinute(matches.some((match) => match.startedAt !== null));

  // Kafelki statystyk — z pelnej listy, niezaleznie od filtrow.
  const liveCount = useMemo(
    () => matches.filter((match) => match.status === 'IN_PROGRESS').length,
    [matches],
  );
  const suspendedCount = matches.length - liveCount;
  const busyCourts = useMemo(
    () =>
      new Set(
        matches
          .filter((match) => match.status === 'IN_PROGRESS' && match.courtId)
          .map((match) => match.courtId),
      ).size,
    [matches],
  );
  const courtsTotal = courtsQuery.data?.length ?? 0;
  const avgPlayTime = useMemo(() => {
    const elapsed = matches
      .filter((match) => match.status === 'IN_PROGRESS')
      .map((match) => elapsedMatchMinutes(match.startedAt, now))
      .filter((minutes): minutes is number => minutes !== null);
    if (elapsed.length === 0) return null;
    return Math.round(elapsed.reduce((sum, minutes) => sum + minutes, 0) / elapsed.length);
  }, [matches, now]);

  const actionMutation = useMutation({
    mutationFn: ({ matchId, action }: { matchId: string; action: LiveAction }) =>
      runMatchAction(matchId, action),
    onSuccess: (_data, { matchId, action }) => {
      // Zmiana statusu przenosi mecz między listami IN_PROGRESS i SUSPENDED.
      void queryClient.invalidateQueries({
        queryKey: competitionKeys.clubMatches(clubId ?? '', { status: 'IN_PROGRESS' }),
      });
      void queryClient.invalidateQueries({
        queryKey: competitionKeys.clubMatches(clubId ?? '', { status: 'SUSPENDED' }),
      });
      void queryClient.invalidateQueries({ queryKey: matchKeys.detail(matchId) });
      showToast({ tone: 'success', description: tt(ACTION_TOAST[action]) });
    },
    onError: (error) =>
      showToast({
        tone: 'danger',
        description:
          error instanceof ApiError ? error.message : tt('matchResult.toast.actionFailed'),
      }),
  });

  /** Naglowek sekcji dziennej: „Dziś", „Wczoraj" albo dzien tygodnia z data. */
  function dayLabel(key: string): string {
    if (key === UNKNOWN_DAY) return '—';
    if (key === todayIso) return t('liveMatchesPage.today');
    if (key === yesterdayIso) return t('liveMatchesPage.yesterday');
    const [year, month, day] = key.split('-').map(Number);
    const weekday = new Intl.DateTimeFormat('pl-PL', { weekday: 'long' }).format(
      new Date(year ?? 0, (month ?? 1) - 1, day ?? 1),
    );
    return `${weekday.charAt(0).toLocaleUpperCase('pl-PL')}${weekday.slice(1)}, ${formatDate(key)}`;
  }

  /** Nazwiska jednej strony jako tekst — do osi czasu. */
  function sideText(ids: readonly string[]): string {
    if (ids.length === 0) return '—';
    return ids.map((id) => names.get(id) ?? '…').join(' / ');
  }

  /** Jedna strona meczu: kolorowe kolka z inicjalami + nazwiska (debel: dwa kolka). */
  function side(ids: readonly string[]) {
    if (ids.length === 0) return <span className="text-fg-subtle">—</span>;
    return (
      <span className="inline-flex items-center gap-1.5">
        {ids.map((id) => (
          <Avatar key={id} name={names.get(id) ?? '…'} size="xs" colorful />
        ))}
        <span>{sideText(ids)}</span>
      </span>
    );
  }

  /** Etykieta fazy: runda drabinki albo grupa. */
  function phaseOf(match: ApiMatch): string {
    if (match.groupNumber === 0) return tt('matchResult.finalGroup');
    if (match.groupNumber !== null) {
      return tt('matchResult.group', { number: match.groupNumber });
    }
    return roundLabel(match.round) ?? '—';
  }

  /** Korty do naglowka osi czasu. */
  const gridCourts: ScheduleCourt[] = useMemo(
    () =>
      (planner?.courts ?? []).map((court) => ({
        id: court.id,
        name: court.name || t('scheduledMatchesPage.court', { number: court.number }),
        ...(court.surface ? { surface: court.surface } : {}),
      })),
    [planner, t],
  );

  /** Proporcjonalna os czasu zbudowana z plannera na wybrany dzien. */
  const timeline: ScheduleTimeline = useMemo(() => {
    if (!planner) return { startMinutes: 0, endMinutes: 0, tiles: [], nowMinutes: null };
    return buildScheduleTimeline(planner, sideText, { todayIso, nowMinutes });
    // sideText zalezy wylacznie od mapy nazwisk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planner, names, todayIso, nowMinutes]);

  /** Klik w mecz na osi czasu — przejscie do wpisania wyniku. */
  function openMatchResult(matchId: string) {
    const competitionId = plannerMatches.find((match) => match.id === matchId)?.competitionId;
    if (competitionId) {
      void navigate(`/turnieje/${competitionId}/mecze/${matchId}/wynik`);
    }
  }

  if (hasNoClubs) {
    return (
      <EmptyState
        icon={<Zap />}
        title={t('liveMatchesPage.noClubsTitle')}
        description={t('liveMatchesPage.noClubsDescription')}
        className="min-h-[24rem]"
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('liveMatchesPage.title')}
        description={t('liveMatchesPage.description')}
      />

      {/* Kafelki statystyk */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile icon={<Zap />} value={liveCount} label={t('liveMatchesPage.stats.live')} />
        <StatTile
          icon={<Pause />}
          value={suspendedCount}
          label={t('liveMatchesPage.stats.suspended')}
        />
        <StatTile
          icon={<MapPin />}
          value={`${busyCourts}/${courtsTotal}`}
          label={t('liveMatchesPage.stats.courtsBusy')}
        />
        <StatTile
          icon={<Timer />}
          value={avgPlayTime !== null ? formatDurationMinutes(avgPlayTime) : '—'}
          label={t('liveMatchesPage.stats.avgTime')}
        />
      </div>

      {/* Pasek narzedzi: filtry listy albo wybor dnia osi czasu + przelacznik widoku */}
      <div className="flex flex-wrap items-center gap-3">
        {view === 'list' ? (
          <>
            <Select
              selectSize="sm"
              aria-label={t('liveMatchesPage.filters.tournament')}
              className="w-52"
              value={tournamentFilter}
              onChange={(event) => setTournamentFilter(event.target.value)}
            >
              <option value="">{t('liveMatchesPage.filters.allTournaments')}</option>
              {(competitionsQuery.data ?? []).map((competition) => (
                <option key={competition.id} value={competition.id}>
                  {competition.name}
                </option>
              ))}
            </Select>
            <Select
              selectSize="sm"
              aria-label={t('liveMatchesPage.filters.court')}
              className="w-44"
              value={courtFilter}
              onChange={(event) => setCourtFilter(event.target.value)}
            >
              <option value="">{t('liveMatchesPage.filters.allCourts')}</option>
              {(courtsQuery.data ?? []).map((court) => (
                <option key={court.id} value={court.id}>
                  {court.name || t('scheduledMatchesPage.court', { number: court.number })}
                </option>
              ))}
            </Select>
            <SegmentedControl
              size="sm"
              label={t('liveMatchesPage.filters.status')}
              value={statusFilter}
              onValueChange={setStatusFilter}
              options={[
                { value: 'all', label: t('liveMatchesPage.filters.statusAll') },
                { value: 'live', label: t('liveMatchesPage.filters.statusLive') },
                { value: 'suspended', label: t('liveMatchesPage.filters.statusSuspended') },
              ]}
            />
          </>
        ) : (
          <>
            <DateInput
              inputSize="sm"
              aria-label={t('scheduledMatchesPage.grid.day')}
              value={plannerDate}
              onChange={(event) => setPlannerDate(event.target.value)}
            />
            <Button size="sm" variant="secondary" onClick={() => setPlannerDate(todayIso)}>
              {t('liveMatchesPage.today')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void navigate('/turnieje/infrastruktura')}
            >
              {t('scheduledMatchesPage.grid.openPlanner')}
            </Button>
          </>
        )}

        <SegmentedControl
          size="sm"
          className="ml-auto"
          label={t('scheduledMatchesPage.filters.view')}
          value={view}
          onValueChange={setView}
          options={[
            { value: 'list', label: t('scheduledMatchesPage.filters.viewList'), icon: <List /> },
            {
              value: 'grid',
              label: t('scheduledMatchesPage.filters.viewGrid'),
              icon: <LayoutGrid />,
            },
          ]}
        />
      </div>

      {/* Widok osi czasu — tylko do odczytu, pelne planowanie w Infrastrukturze */}
      {view === 'grid' && (
        <>
          {plannerQuery.isError && <Alert tone="danger">{t('liveMatchesPage.loadError')}</Alert>}
          {plannerQuery.isPending && <Skeleton className="h-64 w-full" />}
          {planner &&
            (timeline.tiles.length === 0 ? (
              <EmptyState
                icon={<Zap />}
                title={dayLabel(plannerDate)}
                description={t('scheduledMatchesPage.grid.emptyDay')}
                className="min-h-[16rem]"
              />
            ) : (
              <ScheduleGrid
                courts={gridCourts}
                timeline={timeline}
                onMatchClick={openMatchResult}
              />
            ))}
        </>
      )}

      {/* Widok listy */}
      {view === 'list' && (
        <>
          {(liveQuery.isError || suspendedQuery.isError) && (
            <Alert tone="danger">{t('liveMatchesPage.loadError')}</Alert>
          )}
          {(liveQuery.isPending || suspendedQuery.isPending) && (
            <Skeleton className="h-64 w-full" />
          )}

          {liveQuery.isSuccess &&
            suspendedQuery.isSuccess &&
            (matches.length === 0 ? (
              <EmptyState
                icon={<Zap />}
                title={t('liveMatchesPage.title')}
                description={t('liveMatchesPage.empty')}
                className="min-h-[20rem]"
              />
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={<Zap />}
                title={t('liveMatchesPage.title')}
                description={t('liveMatchesPage.filters.noResults')}
                className="min-h-[16rem]"
              />
            ) : (
              <TableContainer>
                <Table className="[&_td]:px-3 [&_th]:px-3">
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>{t('liveMatchesPage.columns.tournament')}</TableHeaderCell>
                      <TableHeaderCell className="w-32">
                        {tt('matchesTab.columns.phase')}
                      </TableHeaderCell>
                      <TableHeaderCell>{tt('matchesTab.columns.players')}</TableHeaderCell>
                      <TableHeaderCell className="w-36">
                        {tt('matchesTab.columns.score')}
                      </TableHeaderCell>
                      <TableHeaderCell className="w-36">
                        {tt('matchesTab.columns.status')}
                      </TableHeaderCell>
                      <TableHeaderCell className="w-32">
                        {t('liveMatchesPage.columns.court')}
                      </TableHeaderCell>
                      <TableHeaderCell className="w-24">
                        {t('liveMatchesPage.columns.playTime')}
                      </TableHeaderCell>
                      <TableHeaderCell className="w-20 text-right">
                        {tt('matchesTab.columns.actions')}
                      </TableHeaderCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {dayGroups.map(([dayKey, dayMatches]) => (
                      <Fragment key={dayKey}>
                        <TableRow className="border-b-0 bg-surface-sunken hover:bg-surface-sunken">
                          <TableCell
                            colSpan={8}
                            className="py-2 text-xs font-semibold tracking-[0.04em] text-fg-strong uppercase"
                          >
                            {dayLabel(dayKey)}
                          </TableCell>
                        </TableRow>

                        {dayMatches.map((match) => {
                          const live = match.status === 'IN_PROGRESS';
                          const phaseAccent =
                            match.groupNumber === null ? roundAccent(match.round) : null;
                          return (
                            <TableRow
                              key={match.id}
                              className="cursor-pointer transition-colors hover:bg-surface-hover"
                              onClick={() =>
                                void navigate(
                                  `/turnieje/${match.competitionId}/mecze/${match.id}/wynik`,
                                )
                              }
                            >
                              <TableCell className="text-sm font-semibold text-fg-strong">
                                {tournamentNames.get(match.competitionId ?? '') ?? '—'}
                              </TableCell>
                              <TableCell
                                className={cn(
                                  'text-sm font-medium whitespace-nowrap',
                                  phaseAccent ?? 'text-fg-muted',
                                )}
                              >
                                <span className="inline-flex items-center gap-1.5">
                                  {match.round === 'F' && match.groupNumber === null && (
                                    <Trophy className="size-3.5" aria-hidden="true" />
                                  )}
                                  {phaseOf(match)}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="inline-flex items-center text-sm text-fg">
                                  {side(match.team1Ids)}
                                  <span className="px-2 text-fg-subtle">vs</span>
                                  {side(match.team2Ids)}
                                </span>
                              </TableCell>
                              <TableCell className="text-sm text-fg tabular-nums">
                                {scoreSummary(match) ?? '—'}
                              </TableCell>
                              <TableCell>
                                {/* Kropka statusu — pulsujaca przy meczach na zywo. */}
                                <Badge tone={live ? 'brand' : 'warning'} size="sm">
                                  <MatchStatusMark status={toViewStatus(match.status)} size="sm" />
                                  {tt(`matchResult.status.${match.status}`)}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {match.courtId ? (
                                  <span
                                    className={cn(
                                      'inline-flex h-6 items-center rounded-full px-2.5 text-xs font-semibold whitespace-nowrap',
                                      courtAccent(match.courtId),
                                    )}
                                  >
                                    {courtNames.get(match.courtId) ?? '—'}
                                  </span>
                                ) : (
                                  <span className="text-sm text-fg-subtle">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-sm text-fg-muted tabular-nums">
                                {(() => {
                                  const elapsed = elapsedMatchMinutes(match.startedAt, now);
                                  return elapsed !== null ? formatDurationMinutes(elapsed) : '—';
                                })()}
                              </TableCell>
                              <TableCell>
                                <div className="flex justify-end gap-1">
                                  {live ? (
                                    <IconButton
                                      size="sm"
                                      variant="secondary"
                                      icon={<Pause />}
                                      label={tt('matchResult.actions.suspend')}
                                      onClick={(event) => {
                                        // Wiersz nawiguje do wyniku — akcja nie ma tam prowadzić.
                                        event.stopPropagation();
                                        actionMutation.mutate({
                                          matchId: match.id,
                                          action: 'suspend',
                                        });
                                      }}
                                    />
                                  ) : (
                                    <IconButton
                                      size="sm"
                                      variant="secondary"
                                      icon={<Play />}
                                      label={tt('matchResult.actions.resume')}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        actionMutation.mutate({
                                          matchId: match.id,
                                          action: 'resume',
                                        });
                                      }}
                                    />
                                  )}
                                  <IconButton
                                    size="sm"
                                    variant="secondary"
                                    icon={<Flag />}
                                    label={tt('matchResult.actions.finish')}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      actionMutation.mutate({
                                        matchId: match.id,
                                        action: 'finish',
                                      });
                                    }}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>

                <TableFooter>{t('liveMatchesPage.footer', { count: filtered.length })}</TableFooter>
              </TableContainer>
            ))}
        </>
      )}
    </div>
  );
}
